import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowLeft } from "lucide-react";
import { PageHero } from "./Properties";
import { Seo } from "@/components/site/Seo";
import { Reveal, MaskLine } from "@/components/site/Reveal";
import { useLang } from "@/lib/i18n";
import { api, mediaUrl } from "@/lib/site";

const L = {
  sk: ["Čítať ďalej", "Späť na blog", "Zatiaľ tu nie sú žiadne príspevky.", "Ďalšie články"],
  en: ["Read more", "Back to blog", "No posts yet.", "More articles"],
  de: ["Weiterlesen", "Zurück zum Blog", "Noch keine Beiträge.", "Weitere Artikel"],
  pl: ["Czytaj dalej", "Powrót do bloga", "Brak wpisów.", "Więcej artykułów"],
  ru: ["Читать далее", "Назад в блог", "Пока нет записей.", "Другие статьи"],
  it: ["Leggi di più", "Torna al blog", "Nessun articolo.", "Altri articoli"],
  es: ["Leer más", "Volver al blog", "Aún no hay artículos.", "Más artículos"],
  he: ["קראו עוד", "חזרה לבלוג", "אין עדיין פוסטים.", "מאמרים נוספים"],
};

const fmtDate = (d, lang) => (d ? new Date(d).toLocaleDateString(lang, { day: "numeric", month: "long", year: "numeric" }) : "");

const PostCard = ({ p, i, s }) => {
  const { lang, tr } = useLang();
  return (
    <motion.div initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.1 }} transition={{ duration: 0.9, delay: (i % 3) * 0.1 }}>
      <Link to={`/blog/${p.slug}`} className="group block" data-testid={`blog-card-${p.slug}`}>
        <div className="aspect-[4/3] overflow-hidden bg-sand/40">
          {p.image && <img src={mediaUrl(p.image)} alt={tr(p.title)} loading="lazy" className="w-full h-full object-cover transition-transform duration-[1.6s] group-hover:scale-110" />}
        </div>
        <div className="text-[10px] tracking-[0.3em] uppercase text-midnight/60 mt-6">{fmtDate(p.created_at, lang)}</div>
        <h2 className="font-display text-3xl text-midnight mt-3 leading-tight group-hover:text-gold transition-colors">{tr(p.title)}</h2>
        {tr(p.excerpt) && <p className="text-sm text-midnight/75 mt-3 leading-relaxed line-clamp-3">{tr(p.excerpt)}</p>}
        <span className="mt-5 inline-flex items-center gap-2 text-[11px] tracking-[0.28em] uppercase text-midnight">{s[0]} <ArrowRight size={14} className="text-gold transition-transform group-hover:translate-x-2" /></span>
      </Link>
    </motion.div>
  );
};

export default function Blog() {
  const { lang } = useLang();
  const s = L[lang] || L.en;
  const [posts, setPosts] = useState(null);
  useEffect(() => { api.get("/posts").then((r) => setPosts(Array.isArray(r.data) ? r.data : [])).catch(() => setPosts([])); }, []);
  return (
    <main data-testid="blog-page">
      <Seo title="Blog" path="/blog" />
      <PageHero img={mediaUrl(posts?.[0]?.image) || "/img/santorini.jpg"} eyebrow="MEVALUX PROPERTY" title="Blog" />
      <section className="bg-ivory py-20 lg:py-28">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
          {posts?.map((p, i) => <PostCard key={p.id} p={p} i={i} s={s} />)}
        </div>
        {posts && !posts.length && <p className="max-w-[1400px] mx-auto px-6 lg:px-12 font-display italic text-2xl text-midnight/70" data-testid="blog-empty">{s[2]}</p>}
      </section>
    </main>
  );
}

export function BlogPost() {
  const { slug } = useParams();
  const { lang, tr } = useLang();
  const s = L[lang] || L.en;
  const [p, setP] = useState(null);
  const [others, setOthers] = useState([]);
  const [err, setErr] = useState(false);
  useEffect(() => {
    setP(null); setErr(false); window.scrollTo(0, 0);
    api.get(`/posts/${slug}`).then((r) => setP(r.data)).catch(() => setErr(true));
    api.get("/posts").then((r) => setOthers((Array.isArray(r.data) ? r.data : []).filter((x) => x.slug !== slug).slice(0, 3))).catch(() => {});
  }, [slug]);
  if (err) return <main className="min-h-screen bg-ivory pt-40 px-6 text-center font-display text-3xl text-midnight">404 · <Link to="/blog" className="text-gold">{s[1]}</Link></main>;
  if (!p) return <main className="min-h-screen bg-midnight" />;
  return (
    <main data-testid="blog-post-page" className="bg-ivory">
      <Seo title={tr(p.title)} description={(tr(p.excerpt) || tr(p.content)).slice(0, 160)} image={mediaUrl(p.image) || "/img/hero.jpg"} path={`/blog/${p.slug}`}
        jsonLd={{ "@context": "https://schema.org", "@type": "BlogPosting", headline: tr(p.title), image: p.image ? [mediaUrl(p.image)] : undefined, datePublished: p.created_at, author: { "@type": "Organization", name: "MEVALUX PROPERTY" } }} />
      <section className="relative h-[70svh] min-h-[460px] bg-midnight overflow-hidden flex items-end">
        {p.image && <motion.img initial={{ scale: 1.15, opacity: 0 }} animate={{ scale: 1, opacity: 0.7 }} transition={{ duration: 1.8 }} src={mediaUrl(p.image)} alt={tr(p.title)} className="absolute inset-0 w-full h-full object-cover" />}
        <div className="absolute inset-0 bg-gradient-to-t from-midnight via-midnight/30 to-midnight/40" />
        <div className="relative max-w-4xl w-full mx-auto px-6 pb-14">
          <Link to="/blog" data-testid="back-to-blog" className="text-[11px] tracking-[0.3em] uppercase text-ivory/80 hover:text-gold flex items-center gap-2 mb-8"><ArrowLeft size={14} /> {s[1]}</Link>
          <div className="text-[10px] tracking-[0.3em] uppercase text-gold mb-4">{fmtDate(p.created_at, lang)}</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-ivory leading-[1.05]" data-testid="blog-post-title"><MaskLine animateNow delay={0.3}>{tr(p.title)}</MaskLine></h1>
        </div>
      </section>
      <article className="max-w-3xl mx-auto px-6 py-16 lg:py-24">
        {tr(p.excerpt) && <Reveal><p className="font-display text-2xl text-midnight leading-[1.5] mb-10">{tr(p.excerpt)}</p></Reveal>}
        <Reveal><div className="text-midnight/85 leading-[1.95] whitespace-pre-line" data-testid="blog-post-content">{tr(p.content)}</div></Reveal>
        <Link to="/blog" className="mt-14 inline-flex items-center gap-2 text-[11px] tracking-[0.3em] uppercase text-midnight hover:text-gold" data-testid="back-to-blog-bottom"><ArrowLeft size={14} className="text-gold" /> {s[1]}</Link>
      </article>
      {others.length > 0 && (
        <section className="border-t border-sand bg-ivory py-20" data-testid="blog-more">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
            <h3 className="font-display text-3xl text-midnight mb-10">{s[3]}</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-10">{others.map((o, i) => <PostCard key={o.id} p={o} i={i} s={s} />)}</div>
          </div>
        </section>
      )}
    </main>
  );
}
